import {
  AiCard,
  AiLoadingPanel,
  AiPanel,
  Button,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextField,
  Tokens,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { useState } from "react";
import {
  listingFieldProposals,
  materialImportSummary,
  variantFieldProposals,
} from "src/routes/libraries/material-catalog/components/materialListingAiProposals";
import type { MaterialListing_DetailFragment } from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import type { MaterialImportAiProposal_ReviewFragment } from "src/routes/libraries/material-catalog/material-import/MaterialImportPage.generated";
import { evictMaterialCatalogListings } from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";
import {
  useClearMaterialListingAiImportMutation,
  useCreateMaterialListingAiImportMutation,
  useDismissMaterialImportAiProposalMutation,
} from "src/routes/libraries/material-catalog/material-listing-page/components/MaterialListingAiImportPanel.generated";

export type MaterialListingAiImportState = "idle" | "processing" | "reviewing" | "failed";

type MaterialListingAiImportPanelProps = {
  /** The listing as saved, which the import is filed under and its findings are compared against */
  listing: MaterialListing_DetailFragment;
  state: MaterialListingAiImportState;
  proposal: MaterialImportAiProposal_ReviewFragment | undefined;
};

/** Start, progress, and review for an AI import from the manufacturer's page. */
export function MaterialListingAiImportPanel(props: MaterialListingAiImportPanelProps) {
  const { state, proposal } = props;
  const tid = useTestIds(props, "materialListingAiImport");
  return (
    <AiPanel rounded padding="lg" {...tid}>
      {state === "processing" ? (
        <AiLoadingPanel
          title="We're importing updates for this material..."
          progressText={proposal?.progressText ?? undefined}
          omitBg
        />
      ) : state === "reviewing" ? (
        <MaterialListingAiImportReview {...props} />
      ) : (
        <MaterialListingAiImportForm {...props} />
      )}
    </AiPanel>
  );
}

/** What the import found, and the way to drop it without applying any of it. */
function MaterialListingAiImportReview(props: MaterialListingAiImportPanelProps) {
  const { listing, proposal } = props;
  const tid = useTestIds(props, "materialListingAiImport");
  const [clearImport, { loading: clearing }] = useClearMaterialListingAiImportMutation();
  const summary = materialImportSummary(listing, listingFieldProposals(proposal), variantFieldProposals(proposal));

  return (
    <AiCard size="sm">
      <div css={Css.df.aic.jcsb.fww.gap2.px3.py2.$}>
        <div css={Css.df.fdc.gapPx(4).fg1.fb0.mwPx(240).$}>
          <span css={Css.smSb.aiBoldText.$} {...tid.reviewTitle}>
            Review updates found in your import.
          </span>
          <span css={Css.xs.color(Tokens.OnSurface).$} {...tid.reviewMessage}>
            {summary}
          </span>
        </div>
        <Button
          label="Clear Import"
          variant="quaternary"
          onClick={async () => {
            // Leaves the listing as it was before the import: proposals rejected, and deleted the variants it created.
            await clearImport({ variables: { id: proposal!.id }, update: evictMaterialCatalogListings });
          }}
          disabled={clearing}
        />
      </div>
    </AiCard>
  );
}

type MaterialListingAiImportReadyModalProps = {
  /** Saves the edits, then loads the import. */
  onSave: () => Promise<void>;
  /** Drops the edits and loads the import. */
  onDiscard: () => void;
};

/**
 * Opened when an import lands while the form has unsaved edits. Loading the import re-reads the
 * listing with its proposals, which would drop those edits, so the user picks what happens to them.
 */
export function MaterialListingAiImportReadyModal(props: MaterialListingAiImportReadyModalProps) {
  const { onSave, onDiscard } = props;
  const { closeModal } = useModal();
  const [saving, setSaving] = useState(false);
  const tid = useTestIds(props, "aiImportReady");

  return (
    <>
      <ModalHeader>Your import is ready</ModalHeader>
      <ModalBody>
        <p css={Css.sm.$} {...tid.message}>
          Blueprint AI finished importing updates for this material. You have unsaved changes. Save or discard your
          changes before reviewing the imported AI updates.
        </p>
      </ModalBody>
      <ModalFooter>
        <Button
          label="Discard Changes"
          variant="tertiaryDanger"
          disabled={saving}
          onClick={() => {
            onDiscard();
            closeModal();
          }}
        />
        <Button
          label="Save and Review"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave();
            } finally {
              setSaving(false);
            }
          }}
        />
      </ModalFooter>
    </>
  );
}

/** The url entry, for starting an import or trying a failed one again. */
function MaterialListingAiImportForm(props: MaterialListingAiImportPanelProps) {
  const { listing, state, proposal } = props;
  const tid = useTestIds(props, "materialListingAiImport");
  // A failed import keeps its url, so trying again doesn't mean retyping it
  const [importUrl, setImportUrl] = useState(proposal?.manufacturerUrl);
  const [createImport, { loading: importing }] = useCreateMaterialListingAiImportMutation();
  const [dismissImport] = useDismissMaterialImportAiProposalMutation();
  const failureReason = state === "failed" ? (proposal?.failureReason ?? "The import failed.") : undefined;

  async function onImport() {
    await createImport({
      variables: { input: { itemId: listing.item.id, manufacturerUrl: importUrl!, materialListingId: listing.id } },
    });
  }

  // A failure has nothing to resolve, so it stays on the listing until it's dismissed or retried
  async function onDismiss() {
    await dismissImport({ variables: { id: proposal!.id } });
  }

  return (
    <AiCard size="lg">
      <div css={Css.df.fdc.gap1.px3.py2.$}>
        <span css={Css.lg.aiBoldText.$} {...tid.title}>
          Update Material Details Via AI Import
        </span>
        <span css={Css.sm.color(Tokens.OnSurface).$} {...tid.message}>
          Update material details by importing data from the manufacturer's website.
        </span>
        {failureReason && (
          <div css={Css.df.aic.jcsb.gap2.$}>
            <span css={Css.sm.red600.$} {...tid.failureReason}>
              {failureReason}
            </span>
            <Button label="Dismiss" variant="tertiary" onClick={onDismiss} />
          </div>
        )}
        <div css={Css.df.aife.gap3.py2.$}>
          <TextField
            label="URL"
            value={importUrl}
            onChange={setImportUrl}
            placeholder="https://www.example.com/products/..."
            disabled={importing}
            fullWidth
          />
          <Button label="Import" disabled={!importUrl || importing} onClick={onImport} />
        </div>
      </div>
    </AiCard>
  );
}
